import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Link } from '../../../../../components/Link';

export interface LanguageUrl {
  locale: 'en' | 'fi' | 'sv';
  url: string;
}

export interface LanguageSwitcherProps {
  languageUrls: {
    all: LanguageUrl[];
  };
  canonicalLocale: string;
  originalJobLanguage?: string | null;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  languageUrls,
  canonicalLocale,
  originalJobLanguage,
}) => {
  const intl = useIntl();

  if (!languageUrls || languageUrls.all.length <= 1) {
    return null;
  }

  const localeNames: Record<'en' | 'fi' | 'sv', string> = {
    en: 'English',
    fi: 'Suomi',
    sv: 'Svenska'
  };

  return (
    <nav 
      className="mb-4 px-1 sm:px-0" 
      aria-label={intl.formatMessage({ 
        id: 'jobDetailPage.languageNavigation', 
        defaultMessage: 'Available languages', 
        description: 'Accessibility label for language selector showing available translations of the job' 
      })}
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          <FormattedMessage 
            id="jobDetailPage.availableLanguages" 
            defaultMessage="Available in:" 
            description="Label prefix for list of languages this job is available in" 
          />
        </span>
        <div className="flex items-center gap-2">
          {languageUrls.all.map(({ locale, url }, index) => {
            const isCurrent = locale === canonicalLocale;
            const isOriginal = originalJobLanguage && locale === originalJobLanguage;
            const isLast = index === languageUrls.all.length - 1;

            return (
              <React.Fragment key={locale}>
                {isCurrent ? (
                  <span className="text-gray-800 dark:text-white font-medium flex items-center gap-1.5">
                    {localeNames[locale]}
                    {isOriginal && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                        (<FormattedMessage 
                          id="jobDetailPage.originalLanguage" 
                          defaultMessage="Original" 
                          description="Label indicating the original language the job was posted in" 
                        />)
                      </span>
                    )}
                  </span>
                ) : (
                  <Link
                    href={url}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline transition-colors flex items-center gap-1.5"
                  >
                    {localeNames[locale]}
                    {isOriginal && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                        (<FormattedMessage 
                          id="jobDetailPage.originalLanguage" 
                          defaultMessage="Original" 
                          description="Label indicating the original language the job was posted in" 
                        />)
                      </span>
                    )}
                  </Link>
                )}
                {!isLast && (
                  <span className="text-gray-400 dark:text-gray-500">•</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
